import { Box, Skeleton, useToast } from '@chakra-ui/react';
import * as Sentry from '@sentry/react';
import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { z } from 'zod';

import {
  finleyAssert,
  utils as commonUtils,
} from '@finley-technologies/common';

import {
  useGetDeliverableQuery,
  useGetUserQuery,
} from 'types/dashboard-core-gqlTypes.generated.d';

import { DeliverableDetailNonResponsibleParty } from './DeliverableDetailNonResponsibleParty';
import { DeliverableDetailResponsibleParty } from './DeliverableDetailResponsibleParty';
import {
  dashboardRoutePadding,
  errorToastConfig,
  errorToastId,
} from 'packages/common-frontend/constants';

// Types
const RouteParamsStruct = z.object({
  deliverableId: z.string({ message: 'Missing URL parameter' }),
});

// Exports
export const DeliverableDetailRoute = () => {
  const navigate = useNavigate();
  const params = useParams();
  const { deliverableId } = RouteParamsStruct.parse(params);
  const toast = useToast();

  finleyAssert(deliverableId, 'deliverableId is required');

  const { data, loading, refetch } = useGetDeliverableQuery({
    fetchPolicy: 'no-cache',
    variables: { deliverableId },
  });

  const { data: userData, loading: userLoading } = useGetUserQuery({
    /**
     *  Programmatically added onError handler.
     * @onError-TODO: Explicitly handle errors for this fetch
     */
    onError: (err) => {
      errorToastConfig.title = commonUtils.EnvUtils.isProduction()
        ? 'An unexpected error occurred. Please contact Finley for support.'
        : err.message;
      toast(errorToastConfig);
    },
  });

  if (
    !data ||
    loading ||
    !data.deliverable ||
    !userData ||
    !userData.user ||
    userLoading
  ) {
    return (
      <Skeleton
        height="600px"
        data-testid="deliverables-detail-route-loading"
      />
    );
  }

  const {
    user: { organizationId: requestingOrganizationId },
  } = userData;

  const {
    deliverable: {
      isDeliverableResponsibleParty,
      isDeliverableViewPermissionedParty,
    },
  } = data;

  if (!isDeliverableResponsibleParty && !isDeliverableViewPermissionedParty) {
    errorToastConfig.title = `You do not have permission to view this deliverable`;
    toast(errorToastConfig);
    navigate('/deliverables');
  }

  // Use this variable for errors that bubble up to the component level route
  // This ensures we only toast the error once when there are multiple errors
  // We should use Chakra's toast.isActive, or a react hook in general, but the state re-renders when there are multiple errors in a given error boundary
  let routeLevelError = false;
  return (
    <Box padding={dashboardRoutePadding}>
      <Sentry.ErrorBoundary
        onError={() => {
          if (!routeLevelError && !toast.isActive(errorToastId)) {
            errorToastConfig.title = `Failed to load deliverables page`;
            routeLevelError = true;
            toast(errorToastConfig);
            navigate('/deliverables');
          }
        }}
      >
        {isDeliverableResponsibleParty ? (
          <DeliverableDetailResponsibleParty
            deliverable={data}
            refetch={refetch}
            requestingOrganizationId={requestingOrganizationId}
          />
        ) : (
          <DeliverableDetailNonResponsibleParty
            deliverable={data}
            requestingOrganizationId={requestingOrganizationId}
          />
        )}
      </Sentry.ErrorBoundary>
    </Box>
  );
};
